import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import dayjs from 'dayjs';
import { AttendanceApi, StaffAttendanceSummaryRow } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useDefaultYear } from './useDefaultYear';

const columnHelper = createColumnHelper<StaffAttendanceSummaryRow>();

const BASE_COLUMNS = [
  columnHelper.accessor('personName', { header: 'Name' }),
  columnHelper.accessor('present', { header: 'Present', cell: (ctx) => <Badge tone="green">{ctx.getValue()}</Badge> }),
  columnHelper.accessor('absent', { header: 'Absent', cell: (ctx) => <Badge tone="red">{ctx.getValue()}</Badge> }),
  columnHelper.accessor('workingDays', { header: 'Working Days', cell: (ctx) => <Badge tone="slate">{ctx.getValue()}</Badge> }),
];

export function TeacherAttendanceSummaryPage() {
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'personName', defaultSortOrder: 'asc' });
  const { selectedYear, isLoading: yearLoading, hasAnyData } = useDefaultYear();

  // Default to the current month-to-date, not the whole academic year's
  // full range — same fix as StudentAttendanceReportPage.tsx/
  // AttendanceSummaryPage.tsx.
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));

  useEffect(() => {
    if (!selectedYear) return;
    const yearStart = selectedYear.startDate.slice(0, 10);
    const yearEnd = selectedYear.endDate.slice(0, 10);
    const monthStart = dayjs().startOf('month').format('YYYY-MM-DD');
    const today = dayjs().format('YYYY-MM-DD');
    if (monthStart >= yearStart && today <= yearEnd) {
      setStartDate(monthStart);
      setEndDate(today);
    } else {
      setStartDate(yearStart);
      setEndDate(yearEnd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear?.id]);

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['attendance', 'teachers-summary', dt.params, startDate, endDate],
    queryFn: () =>
      AttendanceApi.teacherSummary({ ...dt.params, startDate: startDate || undefined, endDate: endDate || undefined }),
    enabled: !yearLoading && !!startDate && !!endDate,
    placeholderData: keepPreviousData,
  });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    ...BASE_COLUMNS,
  ];

  return (
    <>
      <PageHeader title="Teacher Attendance Summary" description="Present/absent totals per teacher over a date range." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name"
            className="pl-9"
          />
        </div>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      {!hasAnyData && !yearLoading ? (
        <div className="card p-8 text-center text-slate-500">No attendance recorded for the selected filters.</div>
      ) : (
        <DataTable
          columns={columns}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No attendance recorded for the selected filters."
          getRowId={(row) => row.personId}
        />
      )}
    </>
  );
}
